import React from 'react';
import styled from 'styled-components';

import Button from 'components/shared/Button';
import InfoChip from 'components/shared/InfoChip';
import DesktopOnly from 'components/shared/responsiveness/DesktopOnly';
import MobileOnly from 'components/shared/responsiveness/MobileOnly';
import useIsMobile from 'hooks/useIsMobile';
import { colors } from 'styles/theme/colors';
import { Label, LabelBold } from 'styles/typography';

export type CoordinateListItemField = {
  label: string;
  value: string;
};

export type CoordinateListItemProps = {
  id: string;
  name: string;
  fields: CoordinateListItemField[];
  tag?: string;
  disabled?: boolean;
  buttonLabel?: string;
  onAddToPortfolio?: (id: string) => void;
};

function CoordinateListItem({
  id,
  name,
  fields,
  tag,
  disabled = false,
  buttonLabel = 'Add to Portfolio',
  onAddToPortfolio,
}: CoordinateListItemProps) {
  const isMobile = useIsMobile();

  return (
    <Container>
      <Content>
        <InfoSection>
          <NameHeading>{name}</NameHeading>
          <SubTextContainer $isMobile={isMobile}>
            {fields.map((field, index) => (
              <React.Fragment key={field.label}>
                <SubText>
                  <CategoryLabel>{field.label}:</CategoryLabel> {field.value}
                </SubText>
                <DesktopOnly>{index < fields.length - 1 && <BulletPoint />}</DesktopOnly>
              </React.Fragment>
            ))}
            <MobileOnly>
              <StyledButton
                variant='outline-primary'
                fullWidth
                disabled={disabled}
                onClick={() => onAddToPortfolio?.(id)}>
                {buttonLabel}
              </StyledButton>
            </MobileOnly>
          </SubTextContainer>
          {tag && (
            <TagRow>
              <InfoChip $size='small' $backgroundColor={colors.pictonBlue10} $color={colors.black75}>
                {tag}
              </InfoChip>
            </TagRow>
          )}
        </InfoSection>
        <DesktopOnly>
          <ButtonSection>
            <StyledButton variant='outline-primary' disabled={disabled} onClick={() => onAddToPortfolio?.(id)}>
              {buttonLabel}
            </StyledButton>
          </ButtonSection>
        </DesktopOnly>
      </Content>
    </Container>
  );
}

export default React.memo(CoordinateListItem);

const Container = styled.div`
  width: 100%;
  background-color: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.black25};
  border-radius: 4px;
  padding: 24px;
  transition: background-color 0.2s ease;
`;

const Content = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
`;

const InfoSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
`;

const NameHeading = styled(LabelBold)`
  font-size: 16px;
  font-weight: var(--font-weight-bold);
  color: ${colors.black};
`;

const SubTextContainer = styled.div<{ $isMobile: boolean }>`
  display: flex;
  flex-direction: ${(props) => (props.$isMobile ? 'column' : 'row')};
  align-items: ${(props) => (props.$isMobile ? 'flex-start' : 'center')};
  gap: 8px;
  flex-wrap: wrap;
`;

const SubText = styled(Label)`
  font-size: 12px;
  line-height: 16px;
  color: ${colors.black};
`;

const CategoryLabel = styled.span`
  color: ${colors.black50};
`;

const BulletPoint = styled.span`
  display: inline-block;
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background-color: ${colors.black50};
  flex-shrink: 0;
  margin: 0 4px;
`;

const TagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const ButtonSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledButton = styled(Button)`
  padding: 8px 24px;
`;
